import React, { useState, useEffect } from 'react';
import { BrandedButton } from "@/components/ui/BrandedButton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useBrandedToast } from '@/hooks/use-branded-toast';
import { CheckCircle2, User, Building, Briefcase, ArrowRight, Feather, Target, RefreshCw, Eye, ChevronDown } from 'lucide-react';
import { progressTrackingService } from '@/services/progressTrackingService';
import { useInteractionTracking } from '@/hooks/useInteractionTracking';
import { analyticsService } from '@/services/analyticsService';
import { useProfile, ProfileData } from '@/hooks/useProfile';
import { motion } from 'framer-motion';



interface MicroProfileFormProps {
    mode?: 'default' | 'onboarding';
    onComplete?: () => void;
}

export const MicroProfileForm: React.FC<MicroProfileFormProps> = ({ mode = 'default', onComplete }) => {
    const { user } = useAuth();
    const { showToast } = useBrandedToast();
    const { trackClick, trackFormSubmit, trackInteraction } = useInteractionTracking('MicroProfileForm');
    const { profile: fetchedProfile, loading: profileLoading, refetch } = useProfile();

    const [saving, setSaving] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [showMissingErrors, setShowMissingErrors] = useState(false);
    const [profile, setProfile] = useState<ProfileData>({
        id: '',
        user_id: '',
        email: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        job_title: '',
        organization_name: '',
        organization_type: '',
        organization_size: '',
        years_experience: '',
        location: '',
        how_did_you_hear: '',
        role: '',
        tech_comfort: '',
        ai_experience: '',
        learning_style: '',
        primary_channel: '',
        key_audience: '',
        reminder_style: '',
        tone_glossary: [],
        onboarding_step: null,
        profile_completed: false
    });
    const [originalProfile, setOriginalProfile] = useState<ProfileData | null>(null);

    useEffect(() => {
        if (fetchedProfile) {
            setProfile(fetchedProfile);
            setOriginalProfile(fetchedProfile);
        }
    }, [fetchedProfile]);

    const handleAnalyze = async () => {
        if (!user) return;
        setAnalyzing(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();

            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-profile`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.access_token}`,
                },
                body: JSON.stringify({ userId: user.id }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to analyze profile');
            }

            const result = await response.json();

            // Update local state with new insights
            if (result.insights) {
                setProfile(prev => ({
                    ...prev,
                    ai_insights: result.insights
                }));
            }

            await refetch();
            showToast({
                type: "success",
                title: "Perception Updated",
                description: "Lyra has refreshed her understanding of your profile."
            });

        } catch (error: any) {
            showToast({
                type: "error",
                title: "Analysis Failed",
                description: error.message
            });
        } finally {
            setAnalyzing(false);
        }
    };

    const handleSave = async () => {
        if (!user) return;

        // SIMPLIFIED: Only first_name and email are required for minimal viable onboarding
        // All other fields are optional for progressive profiling
        const requiredFields = ['first_name'];

        const missing = requiredFields.filter(field =>
            !profile[field as keyof ProfileData] || profile[field as keyof ProfileData].toString().trim() === ''
        );

        if (missing.length > 0) {
            setShowMissingErrors(true);
            showToast({
                type: "error",
                title: "Complete required fields",
                description: "Please add your first name before continuing."
            });
            return;
        }

        setSaving(true);
        try {
            const fieldsToTrack = [
                'tech_comfort', 'learning_style', 'ai_experience', 'role',
                'organization_type', 'organization_size', 'years_experience'
            ];

            const changedFields: string[] = [];

            if (originalProfile) {
                const trackingPromises = fieldsToTrack
                    .filter(field => {
                        const oldVal = originalProfile[field as keyof ProfileData];
                        const newVal = profile[field as keyof ProfileData];
                        const hasChanged = oldVal !== newVal && newVal;
                        if (hasChanged) {
                            changedFields.push(field);
                        }
                        return hasChanged;
                    })
                    .map(field =>
                        progressTrackingService.trackProfileUpdate({
                            userId: user.id,
                            fieldName: field,
                            oldValue: (originalProfile[field as keyof ProfileData] as string) || null,
                            newValue: (profile[field as keyof ProfileData] as string),
                            updateSource: 'profile_page'
                        })
                    );

                Promise.all(trackingPromises).catch(err =>
                    console.warn('Profile update tracking failed (non-fatal):', err)
                );
            }

            const isProfileComplete = requiredFields.every(field =>
                profile[field as keyof ProfileData] && profile[field as keyof ProfileData].toString().trim() !== ''
            );

            trackFormSubmit('profile-form', {
                fieldsChanged: changedFields,
                profileCompleted: isProfileComplete,
                numberOfFieldsChanged: changedFields.length
            });

            analyticsService.trackAnalyticsEvent(
                'profile_update',
                'profile_form_save',
                {
                    fieldsChanged: changedFields,
                    numberOfFieldsChanged: changedFields.length,
                    profileCompleted: isProfileComplete,
                    timestamp: new Date().toISOString()
                }
            );

            const payload = {
                ...profile,
                user_id: user.id,
                id: profile.id || user.id,
                email: profile.email || user.email || null,
                profile_completed: isProfileComplete,
                onboarding_step: isProfileComplete ? 2 : 1,
                tone_glossary: profile.tone_glossary || []
            };

            const { data, error } = await supabase
                .from('profiles')
                .upsert(payload, { onConflict: 'user_id' })
                .select()
                .maybeSingle();

            if (error) throw error;

            const updatedProfile = {
                ...(data || payload),
                profile_completed: isProfileComplete
            } as ProfileData;

            setProfile(updatedProfile);
            setOriginalProfile(updatedProfile);
            setShowMissingErrors(false);

            // Refetch to ensure global state is updated
            await refetch();

            if (mode === 'onboarding' && isProfileComplete) {
                if (onComplete) onComplete();
            } else {
                showToast({
                    type: "success",
                    title: "Profile saved!",
                    description: isProfileComplete
                        ? "Your profile is now complete."
                        : "Profile saved. Complete all required fields to proceed."
                });

                if (mode === 'default') {
                    // Optional: reload only if needed, but refetch should handle it
                    // window.location.reload(); 
                }
            }

        } catch (error: any) {
            showToast({
                type: "error",
                title: "Error",
                description: error.message
            });
        } finally {
            setSaving(false);
        }
    };

    const updateProfile = (field: keyof ProfileData, value: string) => {
        trackInteraction({
            eventType: 'click',
            elementId: `profile-field-${field}`,
            elementType: 'form',
            metadata: {
                fieldName: field,
                action: 'changed',
                hasValue: !!value
            }
        });

        setProfile(prev => ({ ...prev, [field]: value }));
    };

    if (profileLoading) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="animate-pulse flex flex-col items-center">
                    <div className="h-12 w-12 bg-slate-200 rounded-full mb-4"></div>
                    <div className="h-4 w-32 bg-slate-200 rounded"></div>
                </div>
            </div>
        );
    }

    const containerClasses = mode === 'onboarding'
        ? "max-w-2xl mx-auto space-y-4"
        : "space-y-4";

    const sectionClasses = "rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md";
    const requiredNotice = showMissingErrors ? "text-[11px] text-red-600" : "hidden";

    return (
        <div className={containerClasses}>
            {/* Personal Information */}
            <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={sectionClasses}
            >
                {/* AI Living Profile Section - Always visible with empty state */}
                <div className="mb-6 bg-slate-900 rounded-2xl p-5 text-white shadow-xl border border-slate-800">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/10 rounded-full backdrop-blur-sm">
                                <Feather className="w-5 h-5 text-slate-300" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold flex items-center gap-2 text-white">
                                    Lyra's Perception
                                    <Badge variant="outline" className="border-slate-600 text-slate-300 bg-slate-800 text-[10px] px-1.5 py-0">
                                        Beta
                                    </Badge>
                                </h2>
                                <p className="text-slate-400 text-xs mt-0.5">
                                    How Lyra understands your working style.
                                </p>
                            </div>
                        </div>
                        <BrandedButton
                            onClick={handleAnalyze}
                            variant="outline"
                            size="sm"
                            disabled={analyzing}
                            className="bg-white border-white text-slate-900 hover:bg-slate-100 font-medium"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${analyzing ? 'animate-spin' : ''}`} />
                            {analyzing ? 'Analyzing...' : (profile.ai_insights && Object.keys(profile.ai_insights).length > 0) ? 'Refresh Analysis' : 'Generate Insights'}
                        </BrandedButton>
                    </div>

                    {profile.ai_insights && Object.keys(profile.ai_insights).length > 0 ? (
                        <div className="space-y-6">
                            {/* Narrative Bio */}
                            {profile.ai_insights.narrative_bio && (
                                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                                    <p className="text-lg leading-relaxed font-light text-slate-100">
                                        "{profile.ai_insights.narrative_bio}"
                                    </p>
                                </div>
                            )}

                            <div className="grid md:grid-cols-3 gap-3">
                                {/* Strengths */}
                                {profile.ai_insights.strengths && profile.ai_insights.strengths.length > 0 && (
                                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                                        <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Core Strengths</h3>
                                        <div className="flex flex-wrap gap-1.5">
                                            {profile.ai_insights.strengths.map((s, i) => (
                                                <Badge key={i} className="bg-slate-700 text-slate-200 border-none px-2 py-0.5 text-xs">
                                                    {s}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Communication Style */}
                                {profile.ai_insights.communication_style && (
                                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                                        <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Comm Style</h3>
                                        <p className="text-sm font-semibold text-white">{profile.ai_insights.communication_style}</p>
                                    </div>
                                )}

                                {/* Recommended Focus */}
                                {profile.ai_insights.recommended_focus && (
                                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                                        <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Suggested Focus</h3>
                                        <p className="text-sm text-slate-300">{profile.ai_insights.recommended_focus}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                                <p className="text-slate-300 mb-2">Lyra hasn't analyzed your working style yet.</p>
                                <p className="text-sm text-slate-500">Chat with Lyra a few times, then click "Generate Insights" to see what she's learned about you.</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* --- HOW LYRA USES THIS --- */}
                <div className="mb-6">
                    <details className="group" open>
                        <summary className="flex items-center gap-3 cursor-pointer list-none">
                            <div className="p-2 bg-slate-100 rounded-full group-open:bg-slate-200 transition-colors">
                                <Eye className="w-5 h-5 text-slate-900" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-xl font-semibold text-slate-900">How Lyra Uses Your Profile</h2>
                                <p className="text-slate-500 text-sm">See exactly how your data personalizes your experience</p>
                            </div>
                            <ChevronDown className="w-5 h-5 text-slate-400 group-open:rotate-180 transition-transform" />
                        </summary>

                        <div className="mt-4 space-y-3 pl-12">
                            {/* Active Personalizations */}
                            {(profile.current_goal || profile.organization_name || profile.learning_style ||
                              (profile.tone_glossary && profile.tone_glossary.length > 0) || profile.key_audience || profile.tech_comfort) && (
                                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                                    <h3 className="text-sm font-semibold text-green-800 mb-2">Active Personalizations</h3>
                                    <ul className="text-sm text-green-700 space-y-1">
                                        {profile.current_goal && (
                                            <li>• Your goal "{profile.current_goal.length > 50 ? profile.current_goal.slice(0, 50) + '...' : profile.current_goal}" anchors all advice</li>
                                        )}
                                        {profile.organization_name && (
                                            <li>• Examples reference "{profile.organization_name}" by name</li>
                                        )}
                                        {profile.learning_style && (
                                            <li>• Response style: {profile.learning_style}</li>
                                        )}
                                        {profile.tone_glossary && profile.tone_glossary.length > 0 && (
                                            <li>• Tone matches: {profile.tone_glossary.join(', ')}</li>
                                        )}
                                        {profile.key_audience && (
                                            <li>• Content tailored for {profile.key_audience} audience</li>
                                        )}
                                        {profile.tech_comfort && (
                                            <li>• Complexity calibrated to {profile.tech_comfort} tech comfort</li>
                                        )}
                                        {profile.primary_channel && (
                                            <li>• Examples tailored for {profile.primary_channel} communication</li>
                                        )}
                                    </ul>
                                </div>
                            )}

                            {/* Inactive/Missing */}
                            {(!profile.current_goal || !profile.organization_name || !profile.learning_style ||
                              !profile.tone_glossary || profile.tone_glossary.length === 0) && (
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                                    <h3 className="text-sm font-semibold text-slate-600 mb-2">Not Yet Personalized</h3>
                                    <ul className="text-sm text-slate-500 space-y-1">
                                        {!profile.current_goal && <li>• Add a quarterly goal for goal-anchored advice</li>}
                                        {!profile.organization_name && <li>• Add your org name for personalized examples</li>}
                                        {!profile.learning_style && <li>• Set learning style for adapted responses</li>}
                                        {(!profile.tone_glossary || profile.tone_glossary.length === 0) && (
                                            <li>• Add tone preferences for matching communication style</li>
                                        )}
                                        {!profile.primary_channel && <li>• Set primary channel for tailored templates</li>}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </details>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                    {/* --- PERSONAL INFORMATION --- */}
                    <div className="col-span-full mb-2">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded-full">
                                <User className="w-5 h-5 text-slate-900" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                                    Personal Information
                                    {profile.first_name && profile.last_name && (
                                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                                    )}
                                </h2>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="first_name" className="text-slate-600">First Name *</Label>
                        <Input
                            id="first_name"
                            value={profile.first_name}
                            onChange={(e) => updateProfile('first_name', e.target.value)}
                            placeholder="John"
                            className="rounded-xl border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                        />
                        <p className={requiredNotice}>Required</p>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="last_name" className="text-slate-600">Last Name</Label>
                        <Input
                            id="last_name"
                            value={profile.last_name}
                            onChange={(e) => updateProfile('last_name', e.target.value)}
                            placeholder="Doe"
                            className="rounded-xl border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="phone_number" className="text-slate-600">Phone Number</Label>
                        <Input
                            id="phone_number"
                            value={profile.phone_number}
                            onChange={(e) => updateProfile('phone_number', e.target.value)}
                            placeholder="(555) 123-4567"
                            className="rounded-xl border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="location" className="text-slate-600">Location</Label>
                        <Input
                            id="location"
                            value={profile.location}
                            onChange={(e) => updateProfile('location', e.target.value)}
                            placeholder="San Francisco, CA"
                            className="rounded-xl border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                        />
                    </div>

                    {/* --- CURRENT FOCUS --- */}
                    <div className="col-span-full mt-6 mb-2">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded-full">
                                <Target className="w-5 h-5 text-slate-900" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                                    Current Focus
                                </h2>
                                <p className="text-slate-500 text-sm mt-1">
                                    Lyra will frame all advice around this goal.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="col-span-full space-y-2">
                        <Label htmlFor="current_goal" className="text-slate-600">Current Quarterly Goal or Challenge</Label>
                        <Textarea
                            id="current_goal"
                            value={profile.current_goal}
                            onChange={(e) => updateProfile('current_goal', e.target.value)}
                            placeholder="e.g. Launch our detailed End-of-Year Giving Campaign..."
                            className="rounded-xl border-slate-200 focus:border-slate-900 focus:ring-slate-900 min-h-[80px]"
                        />
                    </div>

                    {/* --- PROFESSIONAL CONTEXT --- */}
                    <div className="col-span-full mt-6 mb-2">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded-full">
                                <Building className="w-5 h-5 text-slate-900" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                                    Professional Context
                                    {profile.organization_name && profile.job_title && (
                                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                                    )}
                                </h2>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="organization_name" className="text-slate-600">Organization Name</Label>
                        <Input
                            id="organization_name"
                            value={profile.organization_name}
                            onChange={(e) => updateProfile('organization_name', e.target.value)}
                            placeholder="Helping Hands Foundation"
                            className="rounded-xl border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="job_title" className="text-slate-600">Your Job Title</Label>
                        <Input
                            id="job_title"
                            value={profile.job_title}
                            onChange={(e) => updateProfile('job_title', e.target.value)}
                            placeholder="Program Director"
                            className="rounded-xl border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="organization_type" className="text-slate-600">Organization Type</Label>
                        <Select value={profile.organization_type} onValueChange={(value) => updateProfile('organization_type', value)}>
                            <SelectTrigger className="rounded-xl border-slate-200 focus:ring-slate-900">
                                <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="nonprofit">Non-profit</SelectItem>
                                <SelectItem value="social-enterprise">Social Enterprise</SelectItem>
                                <SelectItem value="government">Government Agency</SelectItem>
                                <SelectItem value="foundation">Foundation</SelectItem>
                                <SelectItem value="community-org">Community Organization</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="organization_size" className="text-slate-600">Organization Size</Label>
                        <Select value={profile.organization_size} onValueChange={(value) => updateProfile('organization_size', value)}>
                            <SelectTrigger className="rounded-xl border-slate-200 focus:ring-slate-900">
                                <SelectValue placeholder="Select size" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="1-10">1-10 employees</SelectItem>
                                <SelectItem value="11-50">11-50 employees</SelectItem>
                                <SelectItem value="51-200">51-200 employees</SelectItem>
                                <SelectItem value="201-500">201-500 employees</SelectItem>
                                <SelectItem value="500+">500+ employees</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="years_experience" className="text-slate-600">Years of Experience</Label>
                        <Select value={profile.years_experience} onValueChange={(value) => updateProfile('years_experience', value)}>
                            <SelectTrigger className="rounded-xl border-slate-200 focus:ring-slate-900">
                                <SelectValue placeholder="Select experience" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="0-1">0-1 years</SelectItem>
                                <SelectItem value="2-5">2-5 years</SelectItem>
                                <SelectItem value="6-10">6-10 years</SelectItem>
                                <SelectItem value="11-15">11-15 years</SelectItem>
                                <SelectItem value="16+">16+ years</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* --- PERSONALIZATION --- */}
                    <div className="col-span-full mt-6 mb-2">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded-full">
                                <Feather className="w-5 h-5 text-slate-900" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-slate-900">Personalization</h2>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="learning_style" className="text-slate-600">Learning Style</Label>
                        <Select value={profile.learning_style} onValueChange={(value) => updateProfile('learning_style', value)}>
                            <SelectTrigger className="rounded-xl border-slate-200 focus:ring-slate-900">
                                <SelectValue placeholder="How do you learn best?" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Action-First">Action-First (Give me the steps)</SelectItem>
                                <SelectItem value="Theory-First">Theory-First (Explain why first)</SelectItem>
                                <SelectItem value="Visual">Visual (Diagrams & lists)</SelectItem>
                                <SelectItem value="Examples">Examples (Show me real scenarios)</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-[11px] text-slate-500">Lyra adapts response format to your preference</p>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="primary_channel" className="text-slate-600">Primary Channel</Label>
                        <Select value={profile.primary_channel} onValueChange={(value) => updateProfile('primary_channel', value)}>
                            <SelectTrigger className="rounded-xl border-slate-200 focus:ring-slate-900">
                                <SelectValue placeholder="Choose a channel" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="email">Email</SelectItem>
                                <SelectItem value="social">Social</SelectItem>
                                <SelectItem value="board_update">Board updates</SelectItem>
                                <SelectItem value="sms">SMS</SelectItem>
                                <SelectItem value="newsletter">Newsletter</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="key_audience" className="text-slate-600">Key Audience</Label>
                        <Select value={profile.key_audience} onValueChange={(value) => updateProfile('key_audience', value)}>
                            <SelectTrigger className="rounded-xl border-slate-200 focus:ring-slate-900">
                                <SelectValue placeholder="Choose an audience" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="donors">Donors</SelectItem>
                                <SelectItem value="board">Board</SelectItem>
                                <SelectItem value="staff">Staff</SelectItem>
                                <SelectItem value="volunteers">Volunteers</SelectItem>
                                <SelectItem value="community">Community</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="reminder_style" className="text-slate-600">Reminder Style</Label>
                        <Select value={profile.reminder_style} onValueChange={(value) => updateProfile('reminder_style', value)}>
                            <SelectTrigger className="rounded-xl border-slate-200 focus:ring-slate-900">
                                <SelectValue placeholder="Pick a cadence" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="quick_win">Quick win nudges</SelectItem>
                                <SelectItem value="deep_dive">Deep dive sessions</SelectItem>
                                <SelectItem value="light">Light check-ins</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="col-span-full space-y-2">
                        <Label htmlFor="tone_glossary" className="text-slate-600">Tone Glossary (comma or space separated)</Label>
                        <Input
                            id="tone_glossary"
                            value={Array.isArray(profile.tone_glossary) ? profile.tone_glossary.join(', ') : ''}
                            onChange={(e) => {
                                const raw = e.target.value;
                                const hasComma = /[,;\n]/.test(raw);
                                const tokens = (hasComma ? raw.split(/[,;\n]+/) : raw.split(/\s+/))
                                    .map((t) => t.trim())
                                    .filter(Boolean);
                                updateProfile('tone_glossary', tokens as any);
                            }}
                            placeholder="warm, clear, donor-friendly"
                            className="rounded-xl border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                        />
                        <p className="text-[11px] text-slate-500">Add traits with commas or spaces. We'll save each as a separate cue.</p>
                    </div>

                </div>
            </motion.section>

            <div className={`flex ${mode === 'onboarding' ? 'justify-center' : 'justify-end'} sticky bottom-6`}>
                <BrandedButton
                    onClick={() => {
                        trackClick('save-profile-btn', 'button', { profileSection: 'all' });
                        handleSave();
                    }}
                    disabled={saving}
                    variant="gradient"
                    loading={saving}
                    className="shadow-lg min-w-[200px]"
                >
                    {saving ? 'Saving...' : mode === 'onboarding' ? 'Complete Profile & Start' : 'Save Profile'}
                    {mode === 'onboarding' && !saving && <ArrowRight className="w-4 h-4 ml-2" />}
                </BrandedButton>
            </div>
        </div>
    );
};
